import React from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import { getOptimizedImageUrl } from '../lib/cms';

export default function OmPage() {
  return (
    <>
      <SeoHead
        title="Om os – Mød Lone Hvid og Niels Boye | Ibjerget på Midtfyn"
        description="Læs om behandlerne på Ibjerget på Midtfyn: Lone Hvid (tarotkortterapi, foredrag, bøger, kunst) og Niels Boye (ALT ansigtsterapi, kropsterapi, healingmassage, hotstone)."
        canonicalUrl="https://www.ibjerget.dk/om-os"
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 bg-[#381d6e] text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/behandlerrum.webp', { width: 1400, quality: 80, fit: 'cover' })}
            alt="Ibjerget behandlerfællesskab på Midtfyn"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#381d6e] via-[#381d6e]/75 to-transparent" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#d9238c]/30 border border-[#d9238c]/40 text-[#f7eefa] text-xs font-semibold uppercase tracking-wider mb-6">
            <i className="fa-solid fa-users" aria-hidden="true"></i>
            <span>Behandlere på Ibjerget</span>
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold text-[#faf5f9] tracking-tight leading-tight">
            Om os
          </h1>
          <p className="mt-6 text-base sm:text-xl text-[#f7eefa] max-w-3xl mx-auto leading-relaxed font-normal">
            Mød Lone Hvid og Niels Boye – to erfarne behandlere med hver deres selvstændige enkeltmandsvirksomhed, forenet om det enkelte menneskes frihed, suverænitet og kropsbevidsthed.
          </p>
        </div>
      </section>

      {/* Værdier & Grundlag */}
      <section className="py-16 sm:py-20 bg-[#faf5f9]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 sm:p-12 border border-[#edd5e8] shadow-sm space-y-5">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#d9238c] block">
              Vores fælles værdisæt
            </span>
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#1f142e] leading-tight">
              Frihed, ansvar og personlig suverænitet
            </h2>
            <div className="prose prose-stone text-base text-[#4e3568] leading-relaxed space-y-4">
              <p>
                Vi har fundet sammen ud fra et fælles værdisæt om at skabe en verden, hvor den enkelte er fri og autentisk. Hvor det enkelte menneske er suverænt.
              </p>
              <p>
                Suverænitet har mange betydninger. Det, vi lægger vægt på i denne sammenhæng, er evnen til at se sig selv, tage ansvar for sig selv, og leve et fuldt og glædesfuldt liv. Og når man tager ansvar for sig selv, har man ofte brug for en eller flere, der selv går vejen, til støtte. Den støtte kan være ved at komme til fuldmåneaftener, foredrag eller individuelle behandlinger.
              </p>
              <blockquote className="border-l-4 border-[#d9238c] pl-4 italic font-serif text-[#381d6e] text-lg my-4">
                "Gå lidt på opdagelse på hjemmesiden og mærk efter, om det er noget for dig."
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* PROFIL 1: LONE HVID */}
      <section id="lone" className="py-20 sm:py-24 bg-white border-t border-[#edd5e8] scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Foto: lone-hvid-profil-ny.webp */}
            <div className="lg:col-span-5 flex flex-col items-center">
              <div className="relative max-w-sm w-full">
                <div className="rounded-3xl overflow-hidden shadow-xl border border-[#edd5e8] bg-[#faf5f9]">
                  <img
                    src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/lone-hvid-profil-ny.webp', { width: 800, quality: 85 })}
                    alt="Portrætfoto af Lone Hvid smilende udendørs med solbriller i håret"
                    className="w-full h-auto object-cover object-top"
                  />
                </div>
              </div>

              {/* Lone kontakt boks */}
              <div className="w-full max-w-sm mt-6 p-6 rounded-2xl bg-[#faf5f9] border border-[#edd5e8] space-y-3 text-center">
                <h3 className="font-serif text-xl font-bold text-[#1f142e]">
                  Lone Hvid
                </h3>
                <p className="text-xs font-semibold text-[#d9238c]">
                  Terapeut, kunstner og forfatter
                </p>
                <div className="text-xs text-[#9b1b66] py-1 px-3 rounded-full bg-white border border-[#edd5e8] inline-block">
                  Enkeltmandsvirksomhed • CVR: 42494445
                </div>
                <div className="pt-2 flex flex-col gap-2.5 text-sm">
                  <a
                    href="tel:30297902"
                    className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-xs shadow transition-colors"
                  >
                    <i className="fa-solid fa-phone"></i>
                    <span>Tlf: 30 29 79 02</span>
                  </a>
                  <a
                    href="mailto:lone.hvid@hotmail.com"
                    className="flex items-center justify-center gap-2 py-2 px-4 rounded-full bg-white hover:bg-[#f0d9eb] text-[#1f142e] font-semibold text-xs border border-[#edd5e8] transition-colors"
                  >
                    <i className="fa-regular fa-envelope"></i>
                    <span>lone.hvid@hotmail.com</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Lone bio */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#faf5f9] text-[#9b1b66] text-xs font-semibold uppercase tracking-wider">
                <i className="fa-solid fa-feather text-xs"></i>
                <span>Terapeut, kunstner og forfatter</span>
              </div>

              <div>
                <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#1f142e] tracking-tight">
                  Lone Hvid
                </h2>
                <p className="text-sm font-medium text-[#d9238c] mt-1">
                  Terapeut, kunstner og forfatter • CVR: 42494445
                </p>
              </div>

              <div className="prose prose-stone text-[#4e3568] text-base leading-relaxed space-y-4">
                <p>
                  Jeg er tidligere folkeskolelærer og har herunder flere års erfaringer med AKT og været skolefe i Kerteminde Kommune.
                </p>
                <p>
                  Jeg er uddannet voksenunderviser, og har arbejdet i flere år som daghøjskolelærer og lærer på Odense Tekniske Skole samt afholdt foredrag og workshops gennem mange år.
                </p>

                {/* Uddannelsesoversigt som overskuelig punktliste */}
                <div className="p-6 bg-[#fcf8fb] rounded-2xl border border-[#edd5e8] space-y-3">
                  <h4 className="font-serif font-semibold text-sm uppercase tracking-wider text-[#d9238c]">
                    Uddannelser & Baggrund:
                  </h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm text-[#4e3568]">
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#d9238c] mt-1 shrink-0 text-xs"></i>
                      <span>Certificeret hypnoterapeut fra Hypnosehuset Danmark, Vejle</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#d9238c] mt-1 shrink-0 text-xs"></i>
                      <span>3-årig NLP-uddannelse</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#d9238c] mt-1 shrink-0 text-xs"></i>
                      <span>PD i psykologi</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#d9238c] mt-1 shrink-0 text-xs"></i>
                      <span>Merkonomkurser i Organisation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#d9238c] mt-1 shrink-0 text-xs"></i>
                      <span>Uddannet shaman fra Shamanhulen, Glamsbjerg</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#d9238c] mt-1 shrink-0 text-xs"></i>
                      <span>Tidligere massør og psykoterapeut i Odense Naturbehandling</span>
                    </li>
                  </ul>
                  {/* Flyttet til bunden hvor den naturligt bruger den fulde bredde */}
                  <div className="pt-2 border-t border-[#edd5e8]/70">
                    <div className="flex items-start gap-2 text-xs sm:text-sm text-[#4e3568]">
                      <i className="fa-solid fa-check text-[#d9238c] mt-1 shrink-0 text-xs"></i>
                      <span>Læreruddannelse, voksenunderviseruddannelse samt AKT-forløb på Odense Seminarium</span>
                    </div>
                  </div>
                </div>

                <p>
                  Tarotkortterapeut og tegneterapeut.
                </p>
                <p>
                  Foredrags- og kursusforløb inden for personlig udvikling.
                </p>
                <p>
                  Erfaringer både med børn og voksne, både som lærer og som terapeut.
                </p>
                <p>
                  Jeg er også forfatter og udgiver spirituelle bøger.
                </p>
                <p>
                  Jeg har i flere år været præstinde i Wiccagruppen Hvide Ravn, og hvis du er interesseret heri, så tag kontakt til mig.
                </p>
                <p>
                  Jeg afholder fuldmåneceremonier, der er åbne for alle. Nogle gange er det andre, der afholder. Tidspunkt og indhold slås op på Ibjergets facebookside samt min egen facebookside.
                </p>
                <p>
                  Jeg arbejder også kunstnerisk, bl.a. med billeder i fjer, uld og silke, og afholder af og til udstillinger og salgsstande. Galleri med mine billeder findes på Ibjergets adresse, men aftal tid for åbning på tlf. 30297902.
                </p>
              </div>

              <div className="pt-2 flex flex-wrap gap-3">
                <Link
                  to="/tarotkortterapi"
                  className="inline-flex items-center gap-2 text-xs font-semibold text-[#d9238c] hover:underline"
                >
                  <span>Læs om Tarotkortterapi</span>
                  <i className="fa-solid fa-arrow-right text-[10px]"></i>
                </Link>
                <span className="text-[#f7eefa]">•</span>
                <Link
                  to="/spirituelle-boeger"
                  className="inline-flex items-center gap-2 text-xs font-semibold text-[#d9238c] hover:underline"
                >
                  <span>Spirituelle bøger & kunst</span>
                  <i className="fa-solid fa-arrow-right text-[10px]"></i>
                </Link>
                <span className="text-[#f7eefa]">•</span>
                <Link
                  to="/foredrag"
                  className="inline-flex items-center gap-2 text-xs font-semibold text-[#d9238c] hover:underline"
                >
                  <span>Foredrag med Lone</span>
                  <i className="fa-solid fa-arrow-right text-[10px]"></i>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* PROFIL 2: NIELS BOYE */}
      <section id="niels" className="py-20 sm:py-24 bg-[#faf5f9] border-t border-[#edd5e8] scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Niels bio */}
            <div className="lg:col-span-7 order-2 lg:order-1 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#f0d9eb] text-[#9b1b66] text-xs font-semibold uppercase tracking-wider">
                <i className="fa-solid fa-spa text-xs"></i>
                <span>Krops- og psykoterapeut</span>
              </div>

              <div>
                <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#1f142e] tracking-tight">
                  Niels Boye
                </h2>
                <p className="text-sm font-medium text-[#d9238c] mt-1">
                  Krops- og psykoterapeut • CVR: 12691807
                </p>
              </div>

              <div className="prose prose-stone text-[#4e3568] text-base leading-relaxed space-y-4">
                <p>
                  Jeg har gennem mange år uddannet mig i to retninger: Den hårde organisatoriske med en BBA kombineret med coaching og psykologi på PD-niveau og den blødere del som NLP Master med speciale i dybere terapi, Practitioner i Systemisk Teori, Healing, Shamanisme, Massage, Ansigtsterapi samt en række andre terapiformer.
                </p>
                <p>
                  Jeg arbejder ikke længere organisatorisk og har nu helliget mig de dybere processer idet jeg har en høj værdi på at være katalysator for mennesker, der ønsker at frisætte eller udvikle deres potentiale.
                </p>
                <p>
                  Jeg kalder det, jeg laver for kropsorienteret psykoterapi, da jeg ikke tilbyder f.eks. Healing eller NLP-terapi separat, men altid med udgangspunkt i kroppen. På den måde sikrer jeg mig, at de frigørelser vi laver er holdbare. Jeg har arbejdet som terapeut i 40 år.
                </p>
                <p className="font-medium text-[#1f142e]">
                  Se mere under de enkelte behandlinger.
                </p>
                <p>
                  Jeg tilbyder Ansigtsterapi, Krops- og Psykoterapi, Healingmassage og Hotstonemassage.
                </p>

                {/* EHS Note */}
                <div className="p-5 bg-white rounded-2xl border-l-4 border-[#d9238c] shadow-sm text-sm text-[#1f142e]">
                  <p className="font-semibold text-xs uppercase tracking-wider text-[#d9238c] mb-1">
                    Vigtig information til klienter:
                  </p>
                  <p>
                    Niels er Elektro Hyper Sensitiv, og derfor skal mobiltelefon og eventuel smartwatch være slukket under behandlingen.
                  </p>
                </div>
              </div>

              <div className="pt-2 flex flex-wrap gap-3">
                <Link
                  to="/alt-ansigtsterapi"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#d9238c] hover:underline"
                >
                  <span>ALT Ansigtsterapi</span>
                  <i className="fa-solid fa-arrow-right text-[10px]"></i>
                </Link>
                <span className="text-[#f7eefa]">•</span>
                <Link
                  to="/kropsterapi"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#d9238c] hover:underline"
                >
                  <span>Kropsterapi</span>
                  <i className="fa-solid fa-arrow-right text-[10px]"></i>
                </Link>
                <span className="text-[#f7eefa]">•</span>
                <Link
                  to="/healingmassage"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#d9238c] hover:underline"
                >
                  <span>Healingmassage</span>
                  <i className="fa-solid fa-arrow-right text-[10px]"></i>
                </Link>
                <span className="text-[#f7eefa]">•</span>
                <Link
                  to="/hotstone-massage"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#d9238c] hover:underline"
                >
                  <span>Hotstone-massage</span>
                  <i className="fa-solid fa-arrow-right text-[10px]"></i>
                </Link>
              </div>
            </div>

            {/* Foto: niels-boye-profil.webp */}
            <div className="lg:col-span-5 order-1 lg:order-2 flex flex-col items-center">
              <div className="relative max-w-sm w-full">
                <div className="rounded-3xl overflow-hidden shadow-xl border border-[#edd5e8] bg-white">
                  <img
                    src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/niels-boye-profil.webp', { width: 800, quality: 85, fit: 'cover' })}
                    alt="Portrætfoto af Niels Boye mod lys neutral baggrund"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>

              {/* Niels kontakt boks */}
              <div className="w-full max-w-sm mt-6 p-6 rounded-2xl bg-white border border-[#edd5e8] space-y-3 text-center shadow-sm">
                <h3 className="font-serif text-xl font-bold text-[#1f142e]">
                  Niels Boye
                </h3>
                <p className="text-xs font-semibold text-[#d9238c]">
                  Krops- og psykoterapeut
                </p>
                <div className="text-xs text-[#9b1b66] py-1 px-3 rounded-full bg-[#faf5f9] border border-[#edd5e8] inline-block">
                  Enkeltmandsvirksomhed • CVR: 12691807
                </div>
                <div className="pt-2 flex flex-col gap-2.5 text-sm">
                  <a
                    href="tel:66157902"
                    className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-xs shadow transition-colors"
                  >
                    <i className="fa-solid fa-phone"></i>
                    <span>Tlf: 66 15 79 02</span>
                  </a>
                  <a
                    href="mailto:niels@ibjerget.dk"
                    className="flex items-center justify-center gap-2 py-2 px-4 rounded-full bg-[#faf5f9] hover:bg-[#f0d9eb] text-[#1f142e] font-semibold text-xs border border-[#edd5e8] transition-colors"
                  >
                    <i className="fa-regular fa-envelope"></i>
                    <span>niels@ibjerget.dk</span>
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
